/**
 * Festschreiben der Urlaubsstunden nach §11 BUrlG in einem Abrechnungszeitraum.
 *
 * Block-gebuchte Urlaubstage liegen ohne festen §11-Wert in der Datenbank
 * (`createVacationEntries` setzt `hours = 0`), weil das Urlaubsentgelt erst zum
 * Urlaubsantritt feststeht. Jede Anzeige berechnet den Wert daher aus dem
 * 13-Wochen-Fenster neu. Solange nichts festgeschrieben ist, verschiebt sich
 * dieser Wert, sobald Arbeitsstunden nachgetragen werden — auch nachdem er ans
 * Lohnbüro gemeldet wurde, und ohne dass es jemandem auffällt.
 *
 * Festschreiben macht die gemeldete Zahl gegen Neuberechnung immun und
 * hinterlässt einen Stand, gegen den sich die Meldung später prüfen lässt.
 * Weil "jetzt ist gemeldet" keine Information ist, die das System selbst hat,
 * ist der Auslöser eine bewusste Aktion in der Oberfläche.
 */

import type { DayEntry } from '../types';
import type { VacationRecalcEntry } from './calculations';
import { isInPayrollPeriod, type PayrollPeriod } from './payrollPeriod';

export interface VacationFinalizationUpdate {
  date: string;
  vacationCalculatedHours: number;
}

export interface VacationFinalizationState {
  /** Tage, die jetzt festgeschrieben werden können. */
  open: VacationFinalizationUpdate[];
  /** Tage, die schon einen festen §11-Wert haben. */
  finalizedCount: number;
  /** Tage in der Zukunft — bleiben absichtlich dynamisch. */
  provisionalCount: number;
  /** Tage ohne Arbeitsdatum im 91-Tage-Fenster. */
  withoutBasisCount: number;
}

/** Anzeige-Zustand eines Abrechnungszeitraums. */
export type VacationFinalizationBadge =
  | 'none'
  | 'open'
  | 'withoutBasis'
  | 'provisional'
  | 'finalized';

/**
 * Ermittelt für einen Abrechnungszeitraum, welche Urlaubstage festgeschrieben
 * werden können und welche bewusst nicht.
 *
 * Nicht festgeschrieben werden:
 * - vorläufige Werte in der Zukunft, weil der Durchschnitt sich noch ändert,
 * - Tage ohne Arbeitsdatum im 91-Tage-Fenster: dort ist die 0 keine Berechnung,
 *   sondern eine fehlende Grundlage. Als Wert festgeschrieben wäre sie eine
 *   Null, die keine Neuberechnung mehr korrigieren kann,
 * - Tage, die bereits einen festen Wert haben — auch eine festgeschriebene 0.
 */
export function getVacationFinalizationState(
  entries: Record<string, DayEntry>,
  recalc: Map<string, VacationRecalcEntry>,
  period: PayrollPeriod
): VacationFinalizationState {
  const open: VacationFinalizationUpdate[] = [];
  let provisionalCount = 0;
  let withoutBasisCount = 0;
  let overwriteProtected = 0;

  for (const [date, computed] of recalc) {
    if (!isInPayrollPeriod(date, period)) continue;

    if (computed.isProvisional) {
      // Ein bereits gesetzter Wert macht einen Zukunftstag nicht endgültig:
      // `recalcFutureVacationHours` überspringt nur finalisierte Tage in der
      // Vergangenheit und berechnet Zukunftstage unabhängig davon neu.
      provisionalCount++;
    } else if (!computed.hasData) {
      withoutBasisCount++;
    } else if (entries[date]?.vacationCalculatedHours !== undefined) {
      // Tritt beim heutigen Verhalten der Neuberechnung nicht auf, weil sie
      // finalisierte Tage der Vergangenheit gar nicht liefert. Bleibt als
      // Schutz stehen: würde sich das ändern, landete hier ein bereits ans
      // Lohnbüro gemeldeter Wert im Schreibvorgang und würde überschrieben.
      overwriteProtected++;
    } else {
      open.push({ date, vacationCalculatedHours: computed.hours });
    }
  }

  // Endgültig festgeschrieben ist ein Tag nur, wenn er einen Wert hat *und* die
  // Neuberechnung ihn nicht mehr anfasst. Die Anwesenheit in der Recalc-Map ist
  // dafür das verlässlichere Kriterium als ein eigener Datumsvergleich: sie
  // kommt aus derselben Funktion, die über die Endgültigkeit entscheidet.
  let finalizedCount = overwriteProtected;
  for (const entry of Object.values(entries)) {
    if (!entry.isVacation && !entry.isHalfVacation) continue;
    if (!isInPayrollPeriod(entry.date, period)) continue;
    if (entry.vacationCalculatedHours === undefined) continue;
    if (recalc.has(entry.date)) continue;
    finalizedCount++;
  }

  open.sort((a, b) => a.date.localeCompare(b.date));

  return { open, finalizedCount, provisionalCount, withoutBasisCount };
}

/**
 * Verdichtet den Zustand auf eine Anzeige-Stufe.
 *
 * Die Reihenfolge folgt der Frage, die HR an die Tabelle stellt: kann ich die
 * Zahl als endgültig abschreiben?
 *
 * 1. `open` — nein, und der Festsetzen-Button behebt es.
 * 2. `withoutBasis` — nein: einzelne Tage gehen mit 0 Std. ein, weil im
 *    91-Tage-Fenster kein Arbeitstag liegt. Kein Button hilft, aber die Zahl
 *    ist womöglich zu niedrig. Muss auch neben festgesetzten Tagen gewinnen,
 *    sonst behauptet `finalized` eine Vollständigkeit, die nicht gilt.
 * 3. `provisional` — noch nicht, löst sich aber von selbst.
 * 4. `finalized` — ja.
 */
export function getVacationFinalizationBadge(
  state: VacationFinalizationState
): VacationFinalizationBadge {
  if (state.open.length > 0) return 'open';
  if (state.withoutBasisCount > 0) return 'withoutBasis';
  if (state.provisionalCount > 0) return 'provisional';
  if (state.finalizedCount > 0) return 'finalized';
  return 'none';
}
